import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
//css
import '../../Canvas.css';
import './Note.css';

//util
import uuid from 'uuid';
import { Handle } from 'reactflow';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { send_request } from '../../../../../utils/Request';
import { saveCanvasEvent } from '../../../../../utils/canvasApi';

//jodit
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CanvasContext } from '../../WorkflowCanvas';

//mui
import { styled, TextField, Tooltip } from '@mui/material';
import { Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FastForwardIcon from '@mui/icons-material/FastForward';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DoneIcon from '@mui/icons-material/Done';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';

//project store to get canvas out
import useProjectStore from '../../../../ProjectCanvas/useProjectStore';
import { Rnd } from 'react-rnd';

import { createSaveEvent } from '../../../../../utils/createSaveEvent';
import NotesLoadingSkeleton from '../../../../../utils/NotesLoadingSkeleton';
import { AddCircle } from '@mui/icons-material';
import {
  getElements,
  getNoteBackendSaveStructure,
  calculateNewItemPostion,
  getLinkBackendSaveStructure,
} from '../../../../../utils/CanvasUtil';
import config from '../../../../../config';
import { handleImagePaste, convertBase64ImagesInMarkdown } from './NoteImagePasteUtil';

// Zustand
import useCanvasStore from '../../useCanvasStore';

const AIDraft = props => (
  <div className={`ai-draft ${props.isTemp == null ? 'ai-draft-hidden' : ''}`}>AI draft</div>
);

const LinkedNote = props => <div className="linked-note">{props.children}</div>;

// Custom code component for syntax highlighting
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  return !inline && language ? (
    <SyntaxHighlighter style={vscDarkPlus} language={language} PreTag="div" {...props}>
      {String(children).replace(/\n$/, '')}
    </SyntaxHighlighter>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

const Note = props => {
    //use context to retrieve canvas and mode
    const { canvasInstance } = useContext(CanvasContext);
    const { modeInfo } = useContext(CanvasContext);
    const { savingInfo } = useContext(CanvasContext);
    const { updated } = useContext(CanvasContext);
    const { notesLoaded, isCanvasLocked } = useContext(CanvasContext);
    const [isNotesLoaded, setIsNotesLoaded] = notesLoaded ? notesLoaded : useState(false);

    const [canvasLocked, setCanvasLocked] = isCanvasLocked ? isCanvasLocked : useState(false);

    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);

    const [mode, setMode] = modeInfo ? modeInfo : useState({});
    const [reactFlowInstance, setReactFlowInstance] = canvasInstance
      ? canvasInstance
      : useState({});
    const [saving, setSaving] = savingInfo ? savingInfo : useState({});
    const [lastModified, setLastModified] = updated ? updated : useState({});
    //css
    const classes = props.classes;
    //state
    const [noteColor, setNoteColor] = useState(props.data.color);
    const [showPicker, setShowPicker] = useState(false);
    const [showResize, setShowResize] = useState(false);
    const [width, setWidth] = useState(props.data.width);
    const [height, setHeight] = useState(props.data.height);
    const [content, setContent] = useState(props.data.content || '');
    const [escapeSave, setEscapeSave] = useState(false);

    const [hasConnection, setHasConnection] = useState(false);
    const isFirstRun = useRef(true);

    const { isNoteClicked } = useContext(CanvasContext);
    const [noteClicked, setNoteClicked] = isNoteClicked ? isNoteClicked : useState(false);
    const [editor, setEditor] = useState(false);
    const textareaRef = useRef(null);
    const base64ConvertedRef = useRef(false);

    // record last modified value for the canvas
    // I am taking out hooks from the conditional because
    // The order of the hooks being called is important, and if we write code that causes hooks to not be called,
    // React will not be able to match the hook call with its values.
    useEffect(() => {
      (async () => {
        let initial = props.data.content;
        // Don't process or update content if initial content is undefined or null
        if (initial === undefined || initial === null) {
          setIsNotesLoaded(true);
          return;
        }

        let currentContent = initial;

        // Replace base64 images with file URLs (only once)
        if (
          !base64ConvertedRef.current &&
          /!\[[^\]]*\]\(data:image\/[a-zA-Z0-9+]+;base64,[^)]+\)/i.test(currentContent)
        ) {
          try {
            const replaced = await convertBase64ImagesInMarkdown(
              currentContent,
              useProjectStore.getState().project_id,
              send_request,
              config.FILE_SERVICE.QUERY_URL
            );
            if (replaced && replaced !== currentContent) {
              currentContent = replaced;
              base64ConvertedRef.current = true;
            }
          } catch (err) {
            console.error('Error converting base64 images', err);
          }
        }

        setContent(currentContent);

        // Persist any conversions to backend
        if (base64ConvertedRef.current) {
          const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);
          if (index !== -1) {
            const nodeItem = useCanvasStore.getState().nodes[index];
            const updated = {
              ...nodeItem,
              data: {
                ...nodeItem.data,
                content: currentContent,
              },
            };
            // Update local state & backend (non-blocking)
            const newNodes = useCanvasStore.getState().nodes.map(n => (n.id === updated.id ? updated : n));
            useCanvasStore.getState().setNodes(newNodes);
            updateNote(updated);
          }
        }

        setIsNotesLoaded(true);
      })();
    }, [props.data.content]);

    const updateNote = async updatedItem => {
      if (saving || !updatedItem || props.data.isTemp === true) return;

      // If reactFlowInstance is not ready yet, skip saving for now to avoid errors
      if (!reactFlowInstance || typeof reactFlowInstance.toObject !== 'function') {
        return;
      }

      setSaving(true);

      const rfiObj = reactFlowInstance.toObject();

      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      // Update the content to the latest whenever we're saving
      if (updatedItem.data) updatedItem.data.content = content;
      let eventData = getNoteBackendSaveStructure(updatedItem);

      //create event for the new note
      let saveEvent = createSaveEvent(
        'UPDATE_NOTE',
        useProjectStore.getState().project_id,
        viewport,
        getElements(reactFlowInstance),
        eventData
      );

      //send event
      await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
        .then(response => {
          if (response.data && response.data.updated) {
            setLastModified(response.data.lastModified);
          }
          if (escapeSave) setEscapeSave(false);
          //insert delay here
          setSaving(false);
          setShowPicker(false);
        })
        .catch(error => {
          console.log(error);
        });
    };

    useEffect(() => {
      setShowPicker(false);
      setShowResize(false);
      setEditor(false);

      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      } else if (!props.selected && mode == 'DEFAULT' && noteClicked === true) {
        setNoteClicked(false);
        var index = nodes.findIndex(x => x.id === props.id);

        // we allow an update if either the content is changed or escape save is not true
        if (index !== -1 && (!escapeSave || content !== nodes[index].data.content)) {
          (async () => {
            const item = nodes[index];

            // If content still contains base64 images, convert before saving
            let finalContent = content;
            if (/!\[[^\]]*\]\(data:image\/[a-zA-Z0-9+]+;base64,[^)]+\)/.test(finalContent)) {
              try {
                finalContent = await convertBase64ImagesInMarkdown(
                  finalContent,
                  useProjectStore.getState().project_id,
                  send_request,
                  config.FILE_SERVICE.QUERY_URL
                );
                setContent(finalContent);
              } catch (err) {
                console.error('Error converting base64 images on auto-save', err);
              }
            }

            let updatedItem;
            const newNodes = nodes.map(n => {
              if (n.id !== item.id) return n;
              updatedItem = {
                ...n,
                data: {
                  ...n.data,
                  color: noteColor,
                  content: finalContent,
                },
                dragHandle: null,
              };
              return updatedItem;
            });

            useCanvasStore.getState().setNodes(newNodes);
            updateNote(updatedItem);

            setEscapeSave(false);
          })();
        }
      }
    }, [props.selected]);

    useEffect(() => {
      if (!editor || mode !== 'DEFAULT') return;
      toggleNoteDragging();
    }, [editor]);

    const toggleNoteDragging = () => {
      // We want to disable dragging for the note now
      let index = nodes.findIndex(x => x.id === props.id);
      if (index === -1) return;

      const item = nodes[index];

      const newNodes = nodes.map(n => {
        if (n.id !== item.id) return n;
        return {
          ...n,
          dragHandle: '.note-drag-handle',
        };
      });

      useCanvasStore.getState().setNodes(newNodes);
    };

    const isViewOnly =
      mode === 'PRODUCTION_CANVAS_VIEW_MODE' ||
      mode === 'VERSION_CANVAS_VIEW_MODE' ||
      mode === 'PROGRESS_CANVAS_MODE' ||
      mode === 'PRODUCTION_CANVAS_MODE';

    if (nodes && mode == 'DEFAULT') {
      //updating note contents
      const changeNoteContents = value => {
        setContent(value);
      };

      const handleOnNoteClick = () => {
        setNoteClicked(true);
      };

      const handleSaveEdits = async () => {
        if (escapeSave) setEscapeSave(false);

        // Ensure any base64 images in the content are converted before saving
        let finalContent = content;
        if (/!\[[^\]]*\]\(data:image\/[a-zA-Z0-9+]+;base64,[^)]+\)/.test(finalContent)) {
          try {
            finalContent = await convertBase64ImagesInMarkdown(
              finalContent,
              useProjectStore.getState().project_id,
              send_request,
              config.FILE_SERVICE.QUERY_URL
            );
            // Update textarea value if user keeps editing after save
            setContent(finalContent);
          } catch (err) {
            console.error('Error converting base64 images on save', err);
          }
        }

        var index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);

        const nodeItem = nodes[index];
        if (nodeItem) {
          const attributes = {
            data: {
              ...nodeItem.data,
              content: finalContent,
            },
            dragHandle: null,
          };

          let updatedItem;
          const newNodes = nodes.map(n => {
            if (n.id !== nodeItem.id) return n;
            updatedItem = {
              ...n,
              data: {
                ...n.data,
                content: finalContent,
              },
              dragHandle: null,
            };
            return updatedItem;
          });

          useCanvasStore.getState().setNodes(newNodes);
          await updateNote(updatedItem);
        }
        setEditor(false);
      };

      const handleDelete = async () => {
        if (saving) return;

        setSaving(true);

        let result = nodes;

        result = result.filter(node => {
          return node.id != props.id && node.target != props.id && node.source != props.id;
        });

        useCanvasStore.getState().setNodes(result);

        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          noteId: props.id,
        };

        let saveEvent = createSaveEvent(
          'DELETE_NOTE',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            //insert delay here
            setSaving(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      const handleChangeNoteColor = color => {
        if (escapeSave) setEscapeSave(false);

        const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);
        if (nodes[index]) {
          const item = nodes[index];

          const updatedNodeItem = {
            ...item,
            data: {
              ...item.data,
              color: color,
            },
          };

          useCanvasStore.getState().updateNode(updatedNodeItem);
        }
        setNoteColor(color);
      };

      const handleCopy = async () => {
        if (saving) return;

        setSaving(true);

        let toCopy = nodes.find(node => node.id == props.id);

        let newNote = {
          id: uuid(),
          type: 'note',
          position: { x: toCopy.position.x + 160, y: toCopy.position.y + 160 },
          data: {
            content: toCopy.data.content,
            color: toCopy.data.color,
            width: toCopy.data.width,
            height: toCopy.data.height,
          },
        };

        useCanvasStore.getState().setNodes([...nodes, newNote]);

        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          note: newNote,
        };

        //create event for the new note
        let saveEvent = createSaveEvent(
          'ADD_NOTE',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            //insert delay here
            setSaving(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      const handleResize = () => {
        if (escapeSave) setEscapeSave(false);

        const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);

        if (nodes[index]) {
          const nodeItem = nodes[index];

          let updatedItem;
          const newNodes = nodes.map(n => {
            if (n.id !== nodeItem.id) return n;
            updatedItem = {
              ...n,
              data: {
                ...n.data,
                width: width,
                height: height,
              },
            };
            return updatedItem;
          });

          useCanvasStore.getState().setNodes(newNodes);
          updateNote(updatedItem);
        }

        setShowResize(false);
      };

      const sendNote = moveToFront => {
        const noteIndex = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);
        let result = [...useCanvasStore.getState().nodes];

        const note = result[noteIndex];
        result.splice(noteIndex, 1);

        if (moveToFront) {
          result.push(note);
        } else {
          result.splice(0, 0, note);
        }
        useCanvasStore.getState().setNodes(result);
        moveNoteFullyBackFront(note.id, moveToFront);
      };

      const moveNoteFullyBackFront = async (currentId, moveToFront) => {
        if (currentId === undefined || moveToFront === undefined) return;

        // new order, that we want to show.
        if (saving) return; // If saving, return

        setSaving(true);
        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          id: 'moveNoteFullyBackFront',
          currentId: currentId,
          moveToFront: moveToFront,
        };

        let saveEvent = createSaveEvent(
          'UPDATE_NOTE',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            setEscapeSave(true);
            //insert delay here
            setSaving(false);
            setShowPicker(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      const handleChangeOrder = down => {
        const pos = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);
        const canvasNodes = [...useCanvasStore.getState().nodes];
        let posToSwap;

        if (down) {
          if (pos === 0) {
            return;
          }

          for (let i = pos - 1; i >= 0; i--) {
            if (canvasNodes[i].type === 'note') {
              posToSwap = i;
              const noteBefore = canvasNodes[posToSwap];
              canvasNodes[posToSwap] = canvasNodes[pos];
              canvasNodes[pos] = noteBefore;
              break;
            }
          }
        } else {
          if (pos === useCanvasStore.getState().nodes.length - 1) {
            return;
          }

          for (let i = pos + 1; i <= canvasNodes.length - 1; i++) {
            if (canvasNodes[i].type === 'note') {
              posToSwap = i;
              const noteAfter = canvasNodes[posToSwap];
              canvasNodes[posToSwap] = canvasNodes[pos];
              canvasNodes[pos] = noteAfter;
              break;
            }
          }
        }
        useCanvasStore.getState().setNodes(canvasNodes);
        updateNotePosition(canvasNodes[posToSwap], canvasNodes[pos]);
      };

      const handleWidth = event => {
        if (escapeSave) setEscapeSave(false);

        let value = parseInt(event.target.value);
        const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);

        if (nodes[index]) {
          const nodeItem = nodes[index];

          let updatedItem;
          const newNodes = nodes.map(n => {
            if (n.id !== nodeItem.id) return n;
            updatedItem = {
              ...n,
              data: {
                ...n.data,
                width: value,
              },
            };
            return updatedItem;
          });

          useCanvasStore.getState().setNodes(newNodes);
          updateNote(updatedItem);
        }
        setWidth(value);
      };

      const handleHeight = event => {
        if (escapeSave) setEscapeSave(false);

        let value = parseInt(event.target.value);
        const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);

        if (nodes[index]) {
          const nodeItem = nodes[index];

          let updatedItem;
          const newNodes = nodes.map(n => {
            if (n.id !== nodeItem.id) return n;
            updatedItem = {
              ...n,
              data: {
                ...n.data,
                height: value,
              },
            };
            return updatedItem;
          });

          useCanvasStore.getState().setNodes(newNodes);
          updateNote(updatedItem);
        }

        setHeight(value);
      };

      const handleSaveColor = async () => {
        if (escapeSave) setEscapeSave(false);
        const index = useCanvasStore.getState().nodes.findIndex(x => x.id === props.id);
        if (nodes[index]) {
          const nodeItem = nodes[index];

          let updatedItem;
          const newNodes = nodes.map(n => {
            if (n.id !== nodeItem.id) return n;
            updatedItem = {
              ...n,
              data: {
                ...n.data,
                color: noteColor,
              },
            };
            return updatedItem;
          });

          useCanvasStore.getState().setNodes(newNodes);
          updateNote(updatedItem);
        }
      };

      const updateNotePosition = async (currentIndex, previousIndex) => {
        if (currentIndex === undefined || previousIndex === undefined) return;

        // new order, that we want to show.
        if (saving) return; // If saving, return

        setSaving(true);
        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let eventData = {
          id: 'swapNotes',
          currentId: currentIndex.id,
          previousId: previousIndex.id,
        };

        let saveEvent = createSaveEvent(
          'UPDATE_NOTE',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            setEscapeSave(true);
            //insert delay here
            setSaving(false);
            setShowPicker(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      const handleAddLinkedNote = async () => {
        if (saving) return;

        setSaving(true);

        let rfiObj = reactFlowInstance.toObject();

        const note = {
          id: uuid(),
          type: 'note',
          position: calculateNewItemPostion(
            props.xPos,
            props.yPos,
            width,
            height,
            200,
            100,
            getElements(reactFlowInstance)
          ),
          data: {
            content: ' ',
            color: props.data.color,
            width: width,
            height: height,
          },
        };

        const link = {
          source: props.id,
          sourceHandle: 'note-right',
          target: note.id,
          targetHandle: 'note-left',
          style: { strokeWidth: '3px', stroke: 'rgba(0,0,0,0.3)' },
          type: 'link',
          markerEnd: {
            type: 'arrowclosed',
            color: '#868686',
          },
          id: uuid(),
        };

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        // Add it to the canvas
        useCanvasStore.getState().setNodes([...nodes, note]);
        useCanvasStore.getState().setEdges([...edges, link]);

        let eventData = {
          notesSection: [getLinkBackendSaveStructure(link), note],
        };

        let saveEvent = createSaveEvent(
          'ADD_MULTIPLE_NOTES',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
          .then(response => {
            if (response.data && response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            setSaving(false);
          })
          .catch(error => {
            console.log(error);
          });
      };

      return (
        <>
          {props.data.isTemp ? <AIDraft isTemp={props.data.isTemp} /> : ''}

          <div
            id={props.id}
            className={`note-wrapper ${props.selected ? 'selected' : ''}`}
            onClick={handleOnNoteClick}
          >
            <Rnd
              className={`rnd-container ${showPicker ? 'nodrag' : ''}`}
              size={{ width: width, height: height }}
              onResize={(e, direction, ref, delta, position) => {
                setWidth(ref.offsetWidth);
                setHeight(ref.offsetHeight);
              }}
              enableResizing={{ bottom: true, right: true, bottomRight: true }}
              resizeHandleClasses={{
                bottomRight: 'nodrag',
                bottom: 'nodrag',
                right: 'nodrag',
              }}
              onResizeStop={(e, direction, ref, delta, position) => {
                handleResize();
              }}
              disableDragging={true}
            >
              <Handle
                id={'note-left'}
                type={'target'}
                position="left"
                className={` note-handle`}
                isConnectable={true}
              />

              <Handle
                id={'note-top'}
                type={'target'}
                position="top"
                className={` note-handle`}
                isConnectable={true}
              />
              <Handle
                id={'note-bottom'}
                type={'source'}
                position="bottom"
                className={` note-handle`}
                isConnectable={true}
              />
              <div
                className={`note note-dynamic ${props.selected && 'note-shadow'} ${
                  editor ? 'note-editing' : ''
                } ${props.data.sentToAI ? 'note-ai-border' : ''}`}
                onDoubleClick={() => {
                  if (isNotesLoaded) {
                    if (props.data.isTemp !== true) {
                      // don't allow edit if content is not loaded
                      setEditor(true);
                    }
                  }
                }}
                style={{
                  '--note-color': noteColor,
                  '--note-width': width < 100 ? '100px' : `${width}px`,
                  '--note-height': height < 100 ? '100px' : `${height}px`,
                }}
              >
                <div className="note-content-container">
                  {!isNotesLoaded || props.data?.content === undefined ? (
                    <NotesLoadingSkeleton />
                  ) : editor ? (
                    <textarea
                      className="note-editor markdown-editor"
                      value={content}
                      onChange={e => changeNoteContents(e.target.value)}
                      ref={textareaRef}
                      onPaste={e =>
                        handleImagePaste(
                          e,
                          textareaRef,
                          content,
                          setContent,
                          useProjectStore.getState().project_id,
                          send_request,
                          config.FILE_SERVICE.QUERY_URL
                        )
                      }
                    />
                  ) : (
                    <div className="note-editor">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          code: CodeBlock,
                        }}
                      >
                        {content}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
              {width && (
                <Handle
                  id={'note-right'}
                  type="source"
                  className={` note-handle`}
                  position="right"
                  isConnectable={true}
                />
              )}
              {props.selected && (
                <div className="note-icons">
                  {editor ? (
                    <Button
                      size={'small'}
                      variant={'contained'}
                      className="note-save-edits"
                      onClick={handleSaveEdits}
                    >
                      save
                    </Button>
                  ) : (
                    <Tooltip title="Edit note">
                      <EditIcon
                        className="note-icon"
                        onClick={event => {
                          if (isNotesLoaded) {
                            if (props.data.isTemp !== true) {
                              // don't allow edit if content is not loaded
                              setEditor(true);
                            }
                          }
                          event.stopPropagation();
                        }}
                      />
                    </Tooltip>
                  )}

                  <Tooltip title="Send to the back">
                    <FastRewindIcon
                      className="note-icon"
                      onClick={event => {
                        sendNote(false);
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Send backwards">
                    <ArrowBackIosIcon
                      className="note-icon"
                      onClick={event => {
                        handleChangeOrder(true);
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Send forwards">
                    <ArrowForwardIosIcon
                      className="note-icon"
                      onClick={event => {
                        handleChangeOrder(false);
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Send to the front">
                    <FastForwardIcon
                      className="note-icon"
                      onClick={event => {
                        sendNote(true);
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  {!showPicker ? (
                    <Tooltip title="Change background color">
                      <CropSquareIcon
                        className="note-icon color-picker-icon"
                        style={{ backgroundColor: props.data.color }}
                        onClick={event => {
                          setShowPicker(!showPicker);
                          event.stopPropagation();
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <DoneIcon
                      className="note-icon"
                      onClick={event => {
                        handleSaveColor();
                        event.stopPropagation();
                      }}
                    />
                  )}
                  <Tooltip title="Duplicate">
                    <FileCopyIcon
                      className="note-icon"
                      onClick={event => {
                        handleCopy();
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <DeleteIcon
                      className="note-icon"
                      onClick={event => {
                        handleDelete();
                        event.stopPropagation();
                      }}
                    />
                  </Tooltip>
                  <div className="note-extra-settings">
                    {showPicker && (
                      <div className="note-color-picker">
                        <HexColorPicker
                          color={noteColor}
                          onChange={color => {
                            handleChangeNoteColor(color);
                          }}
                        />
                        <HexColorInput
                          color={noteColor}
                          onChange={color => {
                            handleChangeNoteColor(color);
                          }}
                        />
                      </div>
                    )}
                    {showResize && (
                      <div className="note-resize">
                        <p className={'s-text'}>Height (px)</p>
                        <TextField
                          size={'small'}
                          type={'number'}
                          value={height}
                          onChange={handleHeight}
                        ></TextField>
                        <p className={'s-text'}>Width (px)</p>
                        <TextField
                          size={'small'}
                          type={'number'}
                          value={width}
                          onChange={handleWidth}
                        ></TextField>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {props.selected && (
                <LinkedNote>
                  <AddCircle
                    onClick={event => {
                      handleAddLinkedNote();
                      event.stopPropagation();
                    }}
                  />
                </LinkedNote>
              )}
            </Rnd>
          </div>
        </>
      );
    } else {
      // View-only mode render
      return (
        <div
          className="note note-view-only"
          style={{
            '--note-color': noteColor,
            '--note-width': width < 100 ? '100px' : `${width}px`,
            '--note-height': height < 100 ? '100px' : `${height}px`,
          }}
        >
          <div className="note-content-container">
            {!isNotesLoaded || !props.data?.content ? (
              <NotesLoadingSkeleton />
            ) : (
              <div className="note-editor view-only">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    code: CodeBlock,
                  }}
                >
                  {props.data.content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      );
    }
};

export default Note;
